import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { notification, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { extend, isEmpty, trim } from 'lodash';
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Modal from "../../../../components/uiComponent/modal/index";
import Stepper from '../../../../components/uiComponent/newStepper';
import Button from "../../../../components/uiComponent/button/index";
import StepsData from "../add-product/stepsData";

import icon from "../../../../assets/icons/close-icon-blue.svg"

import { COLUMN_OPTIONS, EDIT_COLUMN_OPTIONS, mappingTemplateDownloadableFile, poTemplateDownloadableFile, poItemsSkuMappingTemplateDownloadableFile, UPLOAD_MAPPING_OPTIONS, PO_ITEMS_SKU_MAPPING_OPTIONS } from '../../../../utils/download-file';

import { GetPresignedUrl, StoreProductsFile } from '../../../../slices/aws-services';
import { GetVendorsList, StorePoFile, UpdatePoItemsFile, StorePoSkusMapping, StorePoItemsSkuMapping, getLatestFileUpload } from '../../../../slices/po';

import { AddProductManualModalWrapper } from '../style';
import { setItem } from '../../../../slices/auth';

const AddManualProduct = ({
  addProduct,
  setAddProduct,
  refreshGridClick,
  uploadAddProduct,
  editPoItems,
  setEditPoItems = () => { },
  uploadMapping,
  setUploadMapping = () => { },
  addProductFile,
  setAddProductFile = () => { },
  poItemsSkuMapping,
  setPoItemsSkuMapping = () => { }
}) => {
  const dispatch = useDispatch();
  const { poId } = useParams();

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState({ rows: [], columns: [] });
  const [fileRows, setFileRows] = useState([]);
  const [requiredColumns, setRequiredColumns] = useState(COLUMN_OPTIONS);
  const data = requiredColumns.map((col) => ({ label: col.label, data: [] }));
  const [dataToProceed, setDataToProceed] = useState(data);
  const [fileColumns, setFileColumns] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [poName, setPoName] = useState('');
  const {
    vendorsList,
    poDetails,
    pos
  } = useSelector((state) => state.pos || {});

  const { vendor } = poDetails || {};
  const [vendorName, setVendorName] = useState('');

  const [fileUploading, setFileUploading] = useState(false);

  useEffect(() => {
    if (editPoItems && !uploadMapping) setRequiredColumns(EDIT_COLUMN_OPTIONS);
  }, [editPoItems]);

  useEffect(() => {
    if (addProductFile) setRequiredColumns(COLUMN_OPTIONS);
  }, [addProductFile]);

  useEffect(() => {
    if (poItemsSkuMapping) setRequiredColumns(PO_ITEMS_SKU_MAPPING_OPTIONS);
  }, [poItemsSkuMapping]);

  useEffect(() => {
    if (uploadMapping && !editPoItems) setRequiredColumns(UPLOAD_MAPPING_OPTIONS);
  }, [uploadMapping]);

  const clearStates = () => {
    setFileData({ rows: [], columns: [] });
    setFileRows([]);
    setFileColumns([]);
    setFileList([]);
    setPoName('');
    setVendorName('');
    setCurrentStep(0);
    setAddProduct(false);
    refreshGridClick();
    setUploadMapping(false);
    setAddProductFile(false);
    setEditPoItems(false);
    setPoItemsSkuMapping(false);
  };

  const nextStep = () => {
    if (currentStep === 0 && isEmpty(dataToProceed)) {
      const { rows = [], columns = [] } = fileData;
      if (rows?.length) {
        const matchedCols = [];
        const labels = Object.keys(rows[0]);
        const mappedData = [];
        requiredColumns?.forEach(({ label, important = false, optional = true }) => {
          const item = labels?.find(option => option === label);
          let dataToPush = { label, data: [], important, optional };
          if (!isEmpty(item)) {
            dataToPush = { label, value: label, data: rows.map(item => trim(item[label]) || ''), important, optional };
            matchedCols.push(item);
          }
          mappedData.push(dataToPush);
        });
        setDataToProceed(mappedData);
        setFileColumnsToUse(columns);
      } else setDataToProceed([]);
    }
    if (currentStep === 1) {
      handleImportFile();
    }
    if (currentStep === 2) {
      handleSubmitFile();
    }
    currentStep !== 2 && setCurrentStep(currentStep + 1);
  };

  const handleImportFile = () => {
    const requiredColumn = (requiredColumns.filter(col => col.require)).map(col => col.label);
    const filteredData = dataToProceed.filter(row => requiredColumn.includes(row.label) && every(row.data, value => value === ''));
    if (!isEmpty(filteredData)) {
      notification.error({
        message: 'Error',
        error: `${filteredData[0].label} is required!`
      })
    } else {
      parseFileData();
    }
  };

  const convertToXlsx = (fileData) => {
    const parsedData = typeof fileData === 'string' ? Papa.parse(fileData, { header: true }).data : fileData;
    const worksheet = XLSX.utils.json_to_sheet(parsedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const xlsxBinary = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([xlsxBinary], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return blob;
  };

  useEffect(() => {
    dispatch(GetVendorsList());
  }, []);

  const handleSubmitFile = async () => {
    let uploadedFile = {};
    if (!isEmpty(fileList[0])) {
      const fileData = fileRows.filter(row => !row.reason);
      const fileType = fileList[0]?.name.split('.').pop().toLowerCase();
      let data = {};
      if (fileType === 'csv') {
        data = Papa.unparse(fileData);
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        data = convertToXlsx(fileData);
      } else {
        notification.error({
          message: 'File Type Error',
          description: 'Unsupported File Type'
        });
        return;
      }

      if (!editPoItems && !uploadMapping && !poItemsSkuMapping) {
        const matchedEntries = pos.filter(({ file_name }) => file_name === poName);
        if (matchedEntries?.length) {
          return notification.error({
            top: 65,
            message: 'Create PO',
            description: 'PO name must be unique'
          });
        }
      }

      setFileUploading(true);
      dispatch(setItem({
        field: 'showFileHeader',
        value: false
      }));
      await dispatch(GetPresignedUrl({ files: [fileList[0]], fileData: data })).then(({ payload }) => {
        if (!payload?.error && !payload?.error?.error && payload) {
          uploadedFile = payload[0];
          if (uploadedFile) {
            if (poItemsSkuMapping) {
              dispatch(StorePoItemsSkuMapping({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension, poId })).then(({ payload }) => {
                setFileUploading(false);
                if (payload?.error || payload?.error?.error) {
                } else {
                  setCurrentStep((prev) => prev + 1);
                }
              });
            }
            else if (uploadMapping) {
              dispatch(StorePoSkusMapping({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension, poId })).then(({ payload }) => {
                setFileUploading(false);
                if (payload?.error || payload?.error?.error) {
                } else {
                  setCurrentStep((prev) => prev + 1);
                }
              });
            }
            else if (editPoItems) {
              dispatch(UpdatePoItemsFile({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension, poId, type: 'po-item' })).then(({ payload }) => {
                setFileUploading(false);
                if (payload?.error || payload?.error?.error) {
                } else {
                  setCurrentStep((prev) => prev + 1);
                }
              })
            }
            else {
              dispatch(StorePoFile({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension, poName, vendorName: vendorName || vendor?.name, poId: addProductFile ? poId : '' })).then(({ payload }) => {
                setFileUploading(false);
                if (payload?.error || payload?.error?.error) {
                } else {
                  setCurrentStep((prev) => prev + 1);
                }
              })
              // .then(() => {
              //   dispatch(getLatestFileUpload())
              // });
            }
          }
        } else {
          setFileUploading(false);
        }
      });
      // call this after 3 seconds, so that job has process the file
      setTimeout(() => {
        dispatch(setItem({
          field: 'showFileHeader',
          value: true
        }));
      }, 3000);
    }
  };

  const parseFileData = () => {
    const parsedRows = [];
    const identifierSet = new Set();
    const duplicateSet = new Set();

    fileRows.forEach(row => {
      const identifier = row['Identifier']?.toString();
      if (identifier) {
        if (identifierSet.has(identifier)) {
          duplicateSet.add(identifier);
        } else {
          identifierSet.add(identifier);
        }
      }
    });

    fileRows.forEach(row => {
      const allKeys = Object.keys(row);
      for (const key of allKeys) {
        if (uploadMapping) {
          if (key === 'Identifier' && (!row[key] || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Identifier is required' });
          } else if (key === 'Identifier' && (!row[key] || (row[key]?.toString().length !== 12 && row[key]?.toString().length !== 13 && row[key]?.toString().length !== 10 && row[key]?.toString().length !== 14))) {
            extend(row, { reason: 'Identifier length should be 10, 12, 13 or 14' });
          } else if (key === 'Sku' && (!row[key] || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Sku is required' });
          }
        } else if (editPoItems) {
          if (key === 'identifier_value' && (!row[key] || Number(row[key]) <= 0)) {
            extend(row, { reason: 'identifier_value is required' });
          } else if (key === 'identifier_value' && (!row[key] || (row[key]?.toString().length !== 12 && row[key]?.toString().length !== 13 && row[key]?.toString().length !== 10 && row[key]?.toString().length !== 14 ))) {
            extend(row, { reason: 'identifier_value length should be 10, 12, 13 or 14' });
          } else if (key === 'identifier_type' && !row[key]) {
            extend(row, { reason: 'Identifier Type is Required' });
          }
          else if (key === 'identifier_value' && (!row[key] || (row[key]?.toString().length !== 12 && row[key]?.toString().length !== 13 && row[key]?.toString().length !== 10 && row[key]?.toString().length !== 14))) {
            extend(row, { reason: 'identifier_value length should be 10, 12, 13 or 14' });
          } else if (key === 'identifier_value' && (row['identifier_type']?.toString()?.toLowerCase() === 'upc' || row['identifier_type']?.toString()?.toLowerCase() === 'asin') && row[key]?.toString().length !== 12) {
            extend(row, { reason: `${row['identifier_type']?.toString()?.toLowerCase()} must be of 12 length` });
          } else if (key === 'identifier_value' && (row['identifier_type']?.toString()?.toLowerCase() === 'ean' && row[key]?.toString()?.length !== 13)) {
            extend(row, { reason: `${row['identifier_type']?.toString()?.toLowerCase()} must be of 13 length` });
          } else if (key === 'identifier_value' && (row['identifier_type']?.toString()?.toLowerCase() === 'isbn' && (row[key]?.toString()?.length !== 10 && row[key]?.toString()?.length !== 13))) {
            extend(row, { reason: `${row['identifier_type']?.toString()?.toLowerCase()} must be of 10 or 13 length` });
          } else if (key === 'identifier_value' && (row['identifier_type']?.toString()?.toLowerCase() === 'gtin' && row[key]?.toString()?.length !== 14)) {
            extend(row, { reason: `${row['identifier_type']?.toString()?.toLowerCase()} must be of 14 length` });
          }
          else if (key === '"unit_cost"' && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: '"unit_cost" must be the positive numbers' });
          }
          else if (key === '"order_quantity"' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: '"order_quantity" must be a Number' });
          } else if (key === '"invoice_quantity"' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: '"invoice_quantity" must be a Number' });
          } else if (key === '"case_quantity"' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: '"case_quantity" must be a Number' });
          }
        }
        else {
          if (key === 'Identifier' && (!row[key] || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Identifier is required' });
          } else if (key === 'Identifier' && (!row[key] || (row[key]?.toString().length !== 12 && row[key]?.toString().length !== 13 && row[key]?.toString().length !== 10 && row[key]?.toString().length !== 14))) {
            extend(row, { reason: 'Identifier length should be 10, 12, 13 or 14' });
          } else if (key === 'Identifier' && (row['Identifier Type']?.toString()?.toLowerCase() === 'upc' || row['Identifier Type']?.toString()?.toLowerCase() === 'asin') && row[key]?.toString().length !== 12) {
            extend(row, { reason: `${row['Identifier Type']?.toString()?.toLowerCase()} must be of 12 length` });
          } else if (key === 'Identifier' && (row['Identifier Type']?.toString()?.toLowerCase() === 'ean' && row[key]?.toString()?.length !== 13)) {
            extend(row, { reason: `${row['Identifier Type']?.toString()?.toLowerCase()} must be of 13 length` });
          } else if (key === 'Identifier' && (row['Identifier Type']?.toString()?.toLowerCase() === 'isbn' && (row[key]?.toString()?.length !== 10 && row[key]?.toString()?.length !== 13))) {
            extend(row, { reason: `${row['Identifier Type']?.toString()?.toLowerCase()} must be of 10 or 13 length` });
          } else if (key === 'Identifier' && duplicateSet.has(row[key]?.toString())) {
            extend(row, { reason: 'Duplicate Identifier found' });
          } else if (key === 'Identifier Type' && !row[key]) {
            extend(row, { reason: 'Identifier Type is Required' });
          } else if (key === 'Unit Cost' && !row[key]) {
            extend(row, { reason: 'Unit Cost is Required' });
          } else if (key === 'Item Name' && !row[key] && !poItemsSkuMapping) {
            extend(row, { reason: 'Item Name is Required' });
          } else if (key === 'Identifier' && (row['Identifier Type']?.toString()?.toLowerCase() === 'gtin' && row[key]?.toString()?.length !== 14)) {
            extend(row, { reason: `${row['Identifier Type']?.toString()?.toLowerCase()} must be of 14 length` });
          }
          else if (key === 'Unit Cost' && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Unit Cost must be the positive numbers' });
          } else if (key === 'Order Quantity' && !row[key]) {
            extend(row, { reason: 'Order Quantity is Required' });
          } else if (key === 'Brand' && !row[key] && !poItemsSkuMapping) {
            extend(row, { reason: 'Brand is Required' });
          }
          else if (key === 'Order Quantity' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Order Quantity must be a Number' });
          } else if (key === 'Case Quantity' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Case Quantity must be a Number' });
          }
          else if (key === 'Invoice Quantity' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
            extend(row, { reason: 'Invoice Quantity must be a Number' });
          }
        }
      }
      parsedRows.push(row);
    });
    setFileRows(parsedRows);
  };

  const setFileColumnsToUse = (columns) => {
    const cols = columns.map((col) => ({ label: col, value: col }));
    setFileColumns(cols);
  };

  const steps = [
    { title: 'Step 1', description: 'Upload File' },
    { title: 'Step 2', description: 'Data Mapping' },
    { title: 'Step 3', description: 'Review Errors' },
  ];

  const hasMissingValues = (fileRows) => {
    if (editPoItems) {
      if (!fileRows?.length) return true;
      const requiredFields = ['identifier_value', 'identifier_type', 'invoice_quantity', 'unit_cost', 'order_quantity', 'case_quantity'] // all 4 are required for edit po
      let result = fileRows.some((row) =>
        requiredFields.some((field) =>
          row[field] === undefined || row[field] === null || row[field] === '' ||
          (['invoice_quantity', 'received_quantity', 'unit_cost', 'case_quantity', 'extended_cost'] && Number(row[field]) < 0)
        )
      );
      return result
    } else {
      const quantityFields = ['Order Quantity', 'Unit Cost', 'Invoice Quantity', 'Case Quantity'];
      if (!fileRows?.length) return true;
      if (
        fileRows.every(row => !row.Identifier) ||
        fileRows.every(row => !row['Unit Cost']) ||
        fileRows.every(row => !row['Order Quantity']) ||
        // Item Name & Brand are required only for the create flow, not the map-only po-items-sku-mapping
        (!poItemsSkuMapping && fileRows.every(row => !row['Brand'])) ||
        (!poItemsSkuMapping && fileRows.every(row => !row['Item Name'])) ||
        fileRows.every(row => !row['Identifier Type']) ||
        fileRows.some(row =>
          quantityFields.some(field =>
            row[field] != null && row[field] !== '' && Number(row[field]) < 0
          )
        )
      ) {
        return true;
      } else {
        return false
      }
    }
  };

  const hasUploadMappingMissingValues = (fileRows) => {
    if (!fileRows?.length) return true;
    if (
      fileRows.every(row => !row.Identifier) ||
      fileRows.every(row => !row.Sku)) {
      return true;
    } else {
      return false
    }
  };

  const handleDisabled = () => {
    if (
      currentStep === 0 &&
      (!fileList?.length || (!editPoItems && (!vendorName || !poName)))
    ) {
      return true;
    }
    if (currentStep === 1 && hasMissingValues(fileRows)) return true;
    if (
      currentStep === 2 &&
      fileRows.every((row) => row.reason)
    ) {
      return true;
    }
    if (currentStep === 3) {
      return true;
    }

    return false;
  };

  const handleUploadMappingDisabled = () => {
    if (
      currentStep === 0 &&
      (!fileList?.length || (!uploadMapping && !addProductFile && !poItemsSkuMapping))
    ) {
      return true;
    }
    if (currentStep === 1 && uploadMapping && hasUploadMappingMissingValues(fileRows)) return true;
    if (currentStep === 1 && (addProductFile || poItemsSkuMapping) && hasMissingValues(fileRows)) return true;
    if (
      currentStep === 2 &&
      fileRows.every((row) => row.reason)
    ) {
      return true;
    }
    if (currentStep === 3) {
      return true;
    }

    return false;
  };

  return (
    <div>
      <Modal
        open={addProduct}
        onClose={() => {
          setAddProduct(false)
          setCurrentStep(0);
        }}
        title={null}
        header={null}
        footer={null}
        className="add-product-flow"
        closeButton={false}
        width={808}
        onHide={null}
        onSave={() => {
          setAddProduct(false)
        }}
      >
        <AddProductManualModalWrapper>
          <div className='header-wrapper'>
            {
              poItemsSkuMapping ? <span> Add Items &amp; Map SKUs by File Upload </span> :
                addProductFile ? <span> Add Vendor Items by File Upload </span> :
                  <span>
                    {
                      uploadMapping ? 'Upload SKU Mapping' :
                        editPoItems ? 'Edit A PO By File Upload' : 'Create A PO By File Upload'}
                  </span>
            }
            <img onClick={() => clearStates()} className='cursor-pointer' src={icon} />
          </div>
          <Stepper
            currentStep={currentStep}
            steps={steps}
            direction="horizontal"
            size="default"
            status="process"
          />
          {(uploadAddProduct || editPoItems || uploadMapping || addProductFile || poItemsSkuMapping) ?
            <Spin tip='Loading..' spinning={fileUploading}>
              <StepsData
                uploadAddProduct={uploadAddProduct}
                editPoItems={editPoItems}
                setDataToProceed={setDataToProceed}
                setAddProduct={setAddProduct}
                setCurrentStep={setCurrentStep}
                currentStep={currentStep}
                uploadMapping={uploadMapping}
                sampleFileLink={poItemsSkuMapping ? poItemsSkuMappingTemplateDownloadableFile : uploadMapping ? mappingTemplateDownloadableFile : poTemplateDownloadableFile}
                addProductFile={addProductFile}
                poItemsSkuMapping={poItemsSkuMapping}

                setFileData={setFileData}
                setFileList={setFileList}
                fileData={fileData}
                fileRows={fileRows}
                fileList={fileList}

                setFileColumns={setFileColumns}
                setFileRows={setFileRows}
                dataToProceed={dataToProceed}
                fileColumns={fileColumns}
                clearStates={clearStates}
              /> </Spin>
            :
            <Spin tip='Loading' spinning={fileUploading} >
              <StepsData
                sampleFileLink={poTemplateDownloadableFile}
                vendorName={vendorName}
                setAddProduct={setAddProduct}
                currentStep={currentStep}
                setFileData={setFileData}
                setFileList={setFileList}
                fileList={fileList}
                setFileColumns={setFileColumns}
                setDataToProceed={setDataToProceed}
                setFileRows={setFileRows}
                dataToProceed={dataToProceed}
                fileColumns={fileColumns}
                fileData={fileData}
                fileRows={fileRows}
                poName={poName}
                setPoName={setPoName}
                vendorsList={vendorsList}
                setVendorName={setVendorName}
                clearStates={clearStates}
              /></Spin>
          }

          {currentStep === 3 ? null : <footer>
            <Button
              text={currentStep === 2 ? 'Re-Upload File' : 'Back'}
              disabled={currentStep === 0 || fileUploading}
              className="outlined"
              onClick={() => {
                if (currentStep === 2) {
                  setFileData({ rows: [], columns: [] });
                  setFileRows([]);
                  setFileColumns([]);
                  setFileList([]);
                  setCurrentStep(0);
                }
                else setCurrentStep((prev) => prev - 1)
              }}
            />
            <Button
              text={currentStep === 2 ? 'Discard Error  & Proceed' : 'Next'}
              onClick={() => {
                nextStep()
              }}
              disabled={fileUploading || ((!uploadMapping && !addProductFile && !poItemsSkuMapping) ? handleDisabled() : handleUploadMappingDisabled())}
            />
          </footer>}
        </AddProductManualModalWrapper>
      </Modal>

    </div>
  )
}

export default AddManualProduct
